import { useEffect, useState } from 'react';
import { getLectureDetails } from '../../../redux/api/class';
import { isQuestionBlocksMode, LectureData } from '../../lectures/types';
import { getQuestionBlocksPreference, setQuestionBlocksPreference } from '../../../utils/questionBlocksPreference';

interface UseQuestionBlocksToggleOptions {
  /** Pula a checagem (ex.: fluxo de "refazer questões erradas", que não usa o toggle). */
  skip?: boolean;
}

interface UseQuestionBlocksToggleResult {
  /** A aula tem blocos configurados? Enquanto a checagem não responde, é `false`. */
  hasQuestionBlocks: boolean;
  /** Preferência atual do aluno (lembrada por aula). */
  useQuestionBlocks: boolean;
  handleToggleQuestionBlocks: (checked: boolean) => void;
}

/**
 * Estado por trás do checkbox "dividir questões em blocos", compartilhado entre os dois
 * layouts de curso (`DesktopLecture` e `CourseDetailModules`) que dão acesso à mesma prova.
 * `question_blocks_enabled`/`question_blocks` não vêm no payload da listagem do curso, só no
 * detalhe da aula — por isso a checagem leve (`includeQuestions:false`) aqui dentro.
 */
export function useQuestionBlocksToggle(
  classId: number | string | undefined | null,
  { skip = false }: UseQuestionBlocksToggleOptions = {},
): UseQuestionBlocksToggleResult {
  const [useQuestionBlocks, setUseQuestionBlocksState] = useState(() =>
    getQuestionBlocksPreference(classId),
  );
  const [hasQuestionBlocks, setHasQuestionBlocks] = useState(false);

  // Reidrata a preferência se o aluno navegar direto para outra aula sem remontar o
  // componente que usa este hook (o `useState` acima só roda na primeira montagem).
  useEffect(() => {
    setUseQuestionBlocksState(getQuestionBlocksPreference(classId));
  }, [classId]);

  useEffect(() => {
    let cancelled = false;
    setHasQuestionBlocks(false);
    if (skip || !classId) return undefined;

    const checkQuestionBlocks = async () => {
      try {
        const lightLecture: LectureData | undefined = await getLectureDetails(classId, {
          includeQuestions: false,
        });
        if (!cancelled) setHasQuestionBlocks(isQuestionBlocksMode(lightLecture));
      } catch (error) {
        console.error('Erro ao verificar blocos de questões da aula:', error);
      }
    };

    checkQuestionBlocks();

    return () => {
      cancelled = true;
    };
  }, [classId, skip]);

  const handleToggleQuestionBlocks = (checked: boolean) => {
    setUseQuestionBlocksState(checked);
    setQuestionBlocksPreference(classId, checked);
  };

  return { hasQuestionBlocks, useQuestionBlocks, handleToggleQuestionBlocks };
}
